import { Link } from 'react-router-dom';
import { IconArrowLeft, IconCall, IconFillSms } from '@/components/icons/MainIcons';
import { FooterLight } from './Lights';
import styles from '@/style/Footer.module.css';
import FooterPanel from './FooterPanel';
import { useTranslation } from 'react-i18next';
import SocialLinks from '@/components/screens/SocialLinks';
import { getStorageUrl } from '@/utils/utils';
import { FooterProps } from '@/types/global.types';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';

const Footer = ({ data }: { data: FooterProps }) => {
    const { t } = useTranslation();
    const { getLocalizedPath } = useLocalizedPath();

    return (
        <>
            <FooterPanel />
            <footer className={styles.footer}>
                <FooterLight className="ltr:rotate-90 ltr:-translate-x-24" />
                <nav className={`mainContainer ${styles.navWrapper}`}>
                    {/* Logo & Social*/}
                    <div className="flex justify-between gap-4 flex-col">
                        <img
                            loading="lazy"
                            src={getStorageUrl(data?.company_logo)}
                            alt={data?.company_name_ar || data?.company_name_en || 'AMAAN Logo'}
                            className={`${styles.logo}`}
                        />
                        <SocialLinks
                            className="hidden lg:grid lg:grid-cols-5 gap-5 items-center justify-items-center"
                            social={data?.social}
                        />
                    </div>

                    {/* Navigation links ( TWO COLUMNS DESKTOP ) ( SINGLE COLUMN MOBILE ) */}
                    <div>
                        <h3 className={styles.linkH3}>{t('footer.content')}</h3>
                        <ul className={styles.ul}>
                            <FooterLink title={t('menu.home')} to={getLocalizedPath('/')} />
                            <FooterLink title={t('menu.about')} to={getLocalizedPath('/about')} />
                            <FooterLink title={t('menu.projects')} to={getLocalizedPath('/projects')} />
                            <FooterLink title={t('footer.partners')} to={getLocalizedPath('/partners')} />
                            <FooterLink title={t('menu.blog')} to={getLocalizedPath('/blog')} />
                            {/* <FooterLink title={t('footer.contact_us')} to={getLocalizedPath('/contact-us')} /> */}
                        </ul>
                    </div>

                    {/* Communication Links */}
                    <div className="hidden lg:block">
                        <h3 className={styles.linkH3}>{t('footer.communication')}</h3>
                        <ul className=" space-y-6 text-lg text-Grey80 dark:text-white font-normal">
                            <li className="!mt-0 flex items-center gap-2">
                                <IconFillSms className="filter invert dark:invert-0" />
                                <span className="dark:text-white text-bg-gray lg:text-[24px] text-[20px] font-medium">
                                    {data?.email}
                                </span>
                            </li>
                            <li className="flex items-center gap-2">
                                <IconCall className="filter invert dark:invert-0" />
                                <span
                                    className="dark:text-white text-bg-gray lg:text-[24px] text-[20px] font-medium"
                                    dir="ltr"
                                >
                                    {data?.phone}
                                </span>
                            </li>
                        </ul>
                    </div>
                </nav>
                <hr className="mt-10 my-6 bg-Grey20 opacity-15 h-0.5 lg:hidden block" />

                <SocialLinks className="lg:hidden mainContainer" social={data?.social} />

                <hr className="my-6 bg-gray-60 dark:bg-Grey20 dark:opacity-15 h-0.5" />
                <div
                    id="privacy-poclicy"
                    className="flex flex-col lg:flex-row items-center justify-center gap-x-6 gap-y-1.5 pb-5"
                >
                    <Link
                        to={getLocalizedPath('/contact-us')}
                        className="md:text-[20px] text-lg font-normal underline dark:text-Grey20 text-bg-gray"
                    >
                        {t('footer.contact_us')}
                    </Link>
                    <div className="hidden lg:block w-0.5 h-9 bg-black dark:bg-Grey20"></div>
                    <div className="flex gap-4 sm:text-[20px] text-[16px] font-normal underline dark:text-Grey20 text-text-bg-gray">
                        <span>{t('footer.Terms_Conditions')}</span>
                        <span>{t('footer.privacy')}</span>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Footer;

const FooterLink = ({ to = '#', title = '---' }: { to: string; title: string }) => (
    <Link to={to}>
        <li>
            <span>{title}</span>
        </li>
        <IconArrowLeft className="dark:text-bg-arrow text-Grey80" />
    </Link>
);
